'use client';

import { useTranslations } from 'next-intl';
import { useSubscriptionLimits } from '@/features/subscription';
import { cn } from '@/shared/lib/cn';
import { useMaterials } from '@/shared/lib/hooks/useMaterials';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { ReferenceSourceGroup } from './ReferenceSourceGroup';
import type { Material } from '@/types/material';

const MAX_MATERIALS = 5;

export interface MaterialsContentProps {
  selectedMaterialIds: number[] | null;
  onMaterialsChange: (materialIds: number[] | null) => void;
  // Project reference props (optional)
  projectId?: string;
  materialProjectReferenceIds?: string[] | null;
  onMaterialProjectReferencesChange?: (ids: string[] | null) => void;
  // Auto refs toggle (for Auto mode)
  useAutoMaterialRefs?: boolean;
  onUseAutoMaterialRefsChange?: (value: boolean) => void;
  // Layout variant — kept for API parity; both variants now use the same
  // Apple-grouped row idiom (light-gray card + checkmark rows).
  variant?: 'default' | 'compact';
  // Navigate to references management
  onNavigateToReferences?: () => void;
}

export function MaterialsContent({
  selectedMaterialIds,
  onMaterialsChange,
  projectId,
  materialProjectReferenceIds,
  onMaterialProjectReferencesChange,
  useAutoMaterialRefs = false,
  onUseAutoMaterialRefsChange,
  onNavigateToReferences,
}: MaterialsContentProps) {
  const t = useTranslations('render.settings.materials');
  const limits = useSubscriptionLimits();
  const maxReferences = limits?.simultaneous_references || 3;
  const { materials, isLoading, error } = useMaterials();

  const currentIds = selectedMaterialIds || [];
  const hasSelectedRefs = (materialProjectReferenceIds?.length ?? 0) > 0;
  const hasSelectedMaterials = currentIds.length > 0;
  const isAtLimit = currentIds.length >= MAX_MATERIALS;

  const handleMaterialToggle = (materialId: number) => {
    // Library material picks are mutually exclusive with project references:
    // clear any picked refs and drop the reference source to "No references".
    if (hasSelectedRefs) {
      onMaterialProjectReferencesChange?.(null);
    }
    onUseAutoMaterialRefsChange?.(false);

    if (currentIds.includes(materialId)) {
      const updatedIds = currentIds.filter((id) => id !== materialId);
      onMaterialsChange(updatedIds.length > 0 ? updatedIds : null);
    } else {
      if (isAtLimit) return;
      const updatedIds = [...currentIds, materialId].sort((a, b) => a - b);
      onMaterialsChange(updatedIds);
    }
  };

  const handleSelectNone = () => {
    onUseAutoMaterialRefsChange?.(false);
    onMaterialProjectReferencesChange?.(null);
  };

  const handleSelectAll = () => {
    onMaterialsChange(null);
    onMaterialProjectReferencesChange?.(null);
    onUseAutoMaterialRefsChange?.(true);
  };

  const handleSelectiveChange = (ids: string[]) => {
    if (ids.length > 0) {
      onMaterialsChange(null);
    }
    onMaterialProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  return (
    <div className="space-y-4">
      {/* Reference source: No references / All project references /
          Selective references (one always selected). */}
      {projectId && onMaterialProjectReferencesChange && (
        <ReferenceSourceGroup
          projectId={projectId}
          category="material"
          selectedIds={materialProjectReferenceIds || []}
          useAllRefs={useAutoMaterialRefs}
          maxSelection={maxReferences}
          onSelectNone={handleSelectNone}
          onSelectAll={handleSelectAll}
          onSelectiveChange={handleSelectiveChange}
          onNavigateToReferences={onNavigateToReferences}
        />
      )}

      {/* Selection counter — surfaces only when manual selection is active */}
      {hasSelectedMaterials && (
        <p
          className={cn(
            'text-card-meta px-1',
            isAtLimit ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {t('maxSelected', {
            count: currentIds.length,
            max: MAX_MATERIALS,
          })}
        </p>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      )}

      {error && (
        <p className="text-list-item-subtitle text-destructive text-center">
          {error}
        </p>
      )}

      {/* Materials list — Apple-grouped card with checkmark rows. */}
      {!isLoading && !error && materials.length > 0 && (
        <SettingsCard>
          {materials.map((material) => {
            const materialId = parseInt(material.id);
            const isSelected = currentIds.includes(materialId);
            const isDisabled = !isSelected && isAtLimit;

            return (
              <PickerRow
                key={material.id}
                title={material.attributes.name}
                isSelected={isSelected}
                isDisabled={isDisabled}
                onClick={() => handleMaterialToggle(materialId)}
              />
            );
          })}
        </SettingsCard>
      )}
    </div>
  );
}

// Helper to get subtitle for preview. The References row in the main settings
// list collapses the picker, so the user can't see the all/no refs toggle —
// `autoLabel` should be "Auto (all refs)" / "Auto (no refs)" so the active
// variant is visible without expanding. Mirrors Android
// `getAutoModeDisplayText()` and Apple `autoModeLabel()`.
export function getMaterialsSubtitle(
  selectedMaterialIds: number[] | null,
  materials: Material[],
  autoLabel: string
): string {
  const currentIds = selectedMaterialIds || [];
  if (currentIds.length === 0) {
    return autoLabel;
  }

  const selectedNames = currentIds
    .map((id) => {
      const material = materials.find((m) => parseInt(m.id) === id);
      return material ? material.attributes.name : null;
    })
    .filter(Boolean);

  return (
    selectedNames.slice(0, 2).join(', ') +
    (selectedNames.length > 2 ? '...' : '')
  );
}
